<template>
  <div>
    <teleport to="body">
      <div v-show="isOpen" class="clip-show-modal-overlay" data-testid="clip-modal" @click.self="close">
        <div class="clip-show-modal">
          <div class="clip-show-modal-header">
            <div class="clip-show-title">
              {{ headerTitle }}
            </div>
            <button type="button" class="clip-show-close" @click="close">&times;</button>
          </div>
          <div class="clip-show-modal-body">
            <div v-if="isLoading" class="text-center text-muted py-5">
              {{ __("Loading clip...") }}
            </div>
            <div v-else class="clip-show-player-wrapper">
              <div ref="playerContainer" class="clip-show-player" data-testid="clip-modal-player"></div>
              <div v-if="!hasPlayableSource" class="text-center text-muted py-4">
                {{ __("No preview available.") }}
              </div>
            </div>
          </div>
          <div class="clip-show-modal-footer">
          <share-url
            v-if="clip && sharePreviewFetchTemplate && sharePreviewAddTemplate"
            :video-id="clip.id"
            payload-key="clipId"
            :title="clip.video_name || ''"
            :show-file-name="false"
            label="プレビュー共有"
            button-class="btn btn-outline-primary btn-sm"
            :button-full-width="false"
            header-class="mb-0"
            icon-class="fas fa-share-alt"
            :fetch-endpoint="resolveClipEndpoint(sharePreviewFetchTemplate, clip.id)"
            :add-endpoint="resolveClipEndpoint(sharePreviewAddTemplate, clip.id)"
            :share-email-required="shareEmailRequired"
            :share-access-limit-required="shareAccessLimitRequired"
            :share-access-limit-max="shareAccessLimitMax"
            :share-expiry-required="shareExpiryRequired"
          ></share-url>
          <share-url
            v-if="clip && shareDownloadFetchTemplate && shareDownloadAddTemplate"
            :video-id="clip.id"
            payload-key="clipId"
            :title="clip.video_name || ''"
            :show-file-name="false"
            label="DL共有"
            button-class="btn btn-outline-primary btn-sm"
            :button-full-width="false"
            header-class="mb-0"
            icon-class="fas fa-link"
            :fetch-endpoint="resolveClipEndpoint(shareDownloadFetchTemplate, clip.id)"
            :add-endpoint="resolveClipEndpoint(shareDownloadAddTemplate, clip.id)"
            :share-email-required="shareEmailRequired"
            :share-access-limit-required="shareAccessLimitRequired"
            :share-access-limit-max="shareAccessLimitMax"
            :share-expiry-required="shareExpiryRequired"
          ></share-url>
          <a
            v-if="clip && clip.download_url"
            :href="clip.download_url"
            class="btn btn-primary btn-sm"
            data-testid="clip-download"
          >
            {{ __("Download") }}
          </a>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="close">
              {{ __("Close") }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from "axios";
import videojs from "video.js";
import { computeCurrentTimecode } from "../lib/timecode";
import { registerTimecodeComponents, bindSeekBarTimecodeTooltip } from "../lib/videojsTimecodeControls";

export default {
  props: {
    showEndpointTemplate: { type: String, required: true },
    sharePreviewFetchTemplate: { type: String, default: "" },
    sharePreviewAddTemplate: { type: String, default: "" },
    shareDownloadFetchTemplate: { type: String, default: "" },
    shareDownloadAddTemplate: { type: String, default: "" },
    shareEmailRequired: { type: Boolean, default: true },
    shareAccessLimitRequired: { type: Boolean, default: true },
    shareAccessLimitMax: { type: Number, default: 10 },
    shareExpiryRequired: { type: Boolean, default: true },
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
      clip: null,
      player: null,
      requestId: 0,
      timecodeToggleComponent: null,
      timecodeDisplayComponent: null,
    };
  },
  computed: {
    headerTitle() {
      if (!this.clip || !this.clip.id) {
        return this.__("Clip Preview");
      }
      const name = this.clip.video_name ? ` - ${this.clip.video_name}` : "";
      return `${this.__("Clip Preview")} (ID: ${this.clip.id})${name}`;
    },
    hasPlayableSource() {
      return Boolean(this.clip && (this.clip.manifest_url || this.clip.mp4_url));
    },
    hasTimecode() {
      return Boolean(this.clip && this.clip.start_timecode && this.clip.frame_rate);
    },
  },
  methods: {
    open(clipId) {
      this.isOpen = true;
      document.body.style.overflow = "hidden";
      this.clip = null;
      this.isLoading = true;
      this.requestId += 1;
      const currentRequest = this.requestId;
      this.disposePlayer();
      this.$nextTick(() => {
        this.fetchClip(clipId, currentRequest);
      });
    },
    close() {
      this.disposePlayer();
      this.isOpen = false;
      this.isLoading = false;
      this.clip = null;
      document.body.style.overflow = "";
    },
    async fetchClip(clipId, requestId) {
      try {
        const response = await axios.get(this.resolveEndpoint(clipId));
        if (requestId !== this.requestId) {
          return;
        }
        this.clip = response.data?.data ?? null;
      } catch (error) {
        console.error("クリップ取得エラー:", error.response || error);
      } finally {
        if (requestId === this.requestId) {
          this.isLoading = false;
          this.$nextTick(() => {
            this.initPlayer();
          });
        }
      }
    },
    resolveEndpoint(clipId) {
      return this.showEndpointTemplate.replace("__CLIP_ID__", clipId);
    },
    resolveClipEndpoint(template, clipId) {
      if (!template) {
        return "";
      }
      return template.replace("__CLIP_ID__", clipId);
    },
    initPlayer() {
      if (!this.clip || !this.hasPlayableSource) {
        return;
      }
      if (this.player && typeof this.player.isDisposed === "function" && this.player.isDisposed()) {
        this.player = null;
      }
      const container = this.$refs.playerContainer;
      if (!container) {
        return;
      }
      const sourceUrl = this.clip.manifest_url || this.clip.mp4_url;
      const sourceType = this.determineVideoType(sourceUrl);
      if (this.player) {
        this.player.poster(this.clip.thumbnail_url || "");
        this.player.src({ src: sourceUrl, type: sourceType });
        this.player.load();
        this.bindTimecodeControls();
        return;
      }
      const videoElement = document.createElement("video");
      videoElement.className = "video-js vjs-big-play-centered vjs-fluid";
      container.innerHTML = "";
      container.appendChild(videoElement);
      this.player = videojs(videoElement, {
        autoplay: false,
        controls: true,
        preload: "auto",
        fluid: true,
        poster: this.clip.thumbnail_url || "",
        playbackRates: [0.5, 1, 1.5, 2],
        controlBar: {
          pictureInPictureToggle: false,
          skipButtons: { forward: 10, backward: 10 },
          remainingTimeDisplay: false,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          // video.js既定の children 順（playToggle 〜 fullscreenToggle）をベースに、
          // 経過時間/区切り/総時間の表示位置のみ remainingTimeDisplay と同じ
          // 「シークバーの右」に来るよう並び替える。他の子要素は既定を維持する。
          children: [
            'playToggle',
            'skipBackward',
            'skipForward',
            'volumePanel',
            'progressControl',
            'liveDisplay',
            'seekToLive',
            'currentTimeDisplay',
            'timeDivider',
            'durationDisplay',
            'customControlSpacer',
            'playbackRateMenuButton',
            'chaptersButton',
            'descriptionsButton',
            'subsCapsButton',
            'audioTrackButton',
            'pictureInPictureToggle',
            'fullscreenToggle'
          ]
        },
        userActions: {
          hotkeys(event) {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
            event.preventDefault()
            const duration = this.duration() || 0
            const current = this.currentTime() || 0
            const delta = event.key === 'ArrowRight' ? 10 : -10
            this.currentTime(Math.min(Math.max(current + delta, 0), duration))
          }
        },
        html5: {
          vhs: {
            withCredentials: true
          }
        },
        sources: [
          {
            src: sourceUrl,
            type: sourceType,
          },
        ],
      });
      // クリックだけではフォーカスされないため、矢印キーシークをプレーヤー要素フォーカス時のみ発火させる
      this.player.el().setAttribute('tabindex', '0');
      registerTimecodeComponents(videojs);
      this.player.on('timeupdate', this.updateTimecodeDisplay);
      this.player.on('seeked', this.updateTimecodeDisplay);
      this.player.on('loadedmetadata', this.updateTimecodeDisplay);
      this.bindTimecodeControls();
    },
    updateTimecodeDisplay() {
      if (!this.timecodeDisplayComponent || !this.hasTimecode || !this.player) {
        return;
      }
      const currentSeconds = this.player.currentTime() || 0;
      const offsetSeconds = (this.clip.clip_offset_seconds || 0) + currentSeconds;
      const timecodeText = computeCurrentTimecode(this.clip.start_timecode, this.clip.frame_rate, offsetSeconds);
      this.timecodeDisplayComponent.setTimecodeText(timecodeText);
    },
    bindTimecodeControls() {
      if (!this.player) {
        return;
      }
      this.unbindTimecodeControls();
      if (!this.hasTimecode) {
        return;
      }
      const controlBar = this.player.getChild('ControlBar');
      if (!controlBar) {
        return;
      }
      const displayChildNames = controlBar.children().map((c) => c.name());
      const displayFsIndex = displayChildNames.indexOf('FullscreenToggle');
      const displayInsertIndex = displayFsIndex >= 0 ? displayFsIndex : displayChildNames.length;
      this.timecodeDisplayComponent = controlBar.addChild('TimecodeDisplay', {}, displayInsertIndex);

      const toggleChildNames = controlBar.children().map((c) => c.name());
      const toggleFsIndex = toggleChildNames.indexOf('FullscreenToggle');
      const toggleInsertIndex = toggleFsIndex >= 0 ? toggleFsIndex : toggleChildNames.length;
      this.timecodeToggleComponent = controlBar.addChild('TimecodeToggleButton', {}, toggleInsertIndex);
      this.updateTimecodeDisplay();

      bindSeekBarTimecodeTooltip(this.player, () => ({
        startTimecode: this.clip.start_timecode,
        frameRate: this.clip.frame_rate,
        clipOffsetSeconds: this.clip.clip_offset_seconds || 0
      }));
    },
    unbindTimecodeControls() {
      const controlBar = this.player ? this.player.getChild('ControlBar') : null;
      if (controlBar && this.timecodeToggleComponent) {
        controlBar.removeChild(this.timecodeToggleComponent);
        this.timecodeToggleComponent.dispose();
      }
      this.timecodeToggleComponent = null;
      if (controlBar && this.timecodeDisplayComponent) {
        controlBar.removeChild(this.timecodeDisplayComponent);
        this.timecodeDisplayComponent.dispose();
      }
      this.timecodeDisplayComponent = null;
      if (this.player) {
        this.player.removeClass('vjs-timecode-active');
      }
    },
    disposePlayer() {
      this.timecodeToggleComponent = null;
      this.timecodeDisplayComponent = null;
      if (!this.player || (typeof this.player.isDisposed === "function" && this.player.isDisposed())) {
        this.player = null;
        return;
      }
      const container = this.$refs.playerContainer;
      try {
        this.player.pause();
      } finally {
        this.player.dispose();
        this.player = null;
        if (container) {
          container.innerHTML = "";
        }
      }
    },
    determineVideoType(src) {
      if (!src) return "";
      const lowerSrc = src.toLowerCase();
      if (lowerSrc.indexOf(".m3u8") !== -1) {
        return "application/x-mpegURL";
      }
      if (lowerSrc.indexOf(".mp4") !== -1) {
        return "video/mp4";
      }
      return "";
    },
  },
  beforeUnmount() {
    this.disposePlayer();
  },
};
</script>

<style scoped>
.clip-show-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20000;
}

.clip-show-modal {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 20001;
}

.clip-show-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
}

.clip-show-title {
  font-weight: 600;
}

.clip-show-close {
  font-size: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  color: #666;
}

.clip-show-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.clip-show-player {
  max-width: 720px;
  margin: 0 auto;
}

.clip-show-player .video-js {
  width: 100%;
  max-height: 420px;
}

.clip-show-modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #e6e6e6;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
